import type { ComputedRef, Ref, WritableComputedRef } from 'vue';
import {
  computed,
} from 'vue';

import type { IApiFormErrors } from '@modules/core/services/query/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IReportQueryOptions, IReportTabItem } from '@modules/reports/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { useReportUploadFileMutation } from '@modules/reports/mutations';

import type { IReportUploadData, IReportUploadFileEmits } from '../types';

export interface ReportUploadFileComposable {
  isLoading: Ref<boolean>;
  errors: Ref<IApiFormErrors | undefined>;
  showDialog: WritableComputedRef<boolean>;
  uploadSchema: ComputedRef<FormSchema<IReportUploadData>>;
  clearError(key: string): void;
  clearAllErrors(): void;
  onSubmit(data: IReportUploadData): Promise<void>;
  close(): void;
}
export default function useReportUploadFile(
  activeTab: Ref<IReportTabItem>,
  activeInnerTab: Ref<IReportTabItem>,
  queryOptions: Ref<IReportQueryOptions>,
  visible: Ref<boolean>,
  emit: IReportUploadFileEmits,
): ReportUploadFileComposable {
  const {
    isLoading,
    mutateAsync: uploadFile,
    errors,
    clearError,
    clearAllErrors,
  } = useReportUploadFileMutation();

  const showDialog = computed<boolean>({
    get: () => visible.value,
    set: (value) => emit('toggle-dialog', value),
  });

  const uploadSchema = computed<FormSchema<IReportUploadData>>(() => ({
    file: {
      type: EFormInputTypes.SINGLE_FILE,
      format: 'csv',
      accept: '.csv',
      showUploadButton: true,
    },
  }));

  async function onSubmit({ file }: IReportUploadData): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    await uploadFile({
      formData,
      options: queryOptions.value,
      tabId: activeTab.value.id,
      innerTabId: activeInnerTab.value.id,
    });

    close();
  }

  function close(): void {
    showDialog.value = false;
  }

  return {
    isLoading,
    errors,
    showDialog,
    uploadSchema,
    clearError,
    clearAllErrors,
    onSubmit,
    close,
  };
}
